import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Auth from "../../../public/images/Auth";
import Mail from "../../../public/images/Mail";
import Phone from "../../../public/images/Phone";
import UserAnimation from "../../../public/images/UserAnimation";
import "./animationPanel.scss";

export default function AnimationPanel({
  sceneActive,
  overlayVisible,
  phoneVisible,
  authVisible,
  emailVisible,
  platformFormKey,
  companyScene,
  companySceneKey,
  overlayAlreadyShown,
  wasReload,
  restoredCompanyPhase = 0,
  onCompanyPhaseChange,

  onSceneDone,
}) {
  const baseSceneMs = 1200;
  const companySceneMs = 2300;
  const SCENE_MS = companyScene ? companySceneMs : baseSceneMs;

  const skipReplayAnimations = wasReload || overlayAlreadyShown;
  const hasAnimatedInRef = useRef(skipReplayAnimations);

  const [companyPhase, setCompanyPhase] = useState(restoredCompanyPhase || 0);

  useEffect(() => {
    onCompanyPhaseChange?.(companyPhase);
  }, [companyPhase, onCompanyPhaseChange]);

  useEffect(() => {
    if (!sceneActive) return;

    if (companyScene) {
      setCompanyPhase(1);

      const t2 = setTimeout(() => setCompanyPhase(2), 520);
      const t3 = setTimeout(() => setCompanyPhase(3), 1100);
      const t4 = setTimeout(() => setCompanyPhase(4), 1450);

      const done = setTimeout(() => onSceneDone?.(), SCENE_MS);

      return () => {
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(t4);
        clearTimeout(done);
      };
    }

    const t = setTimeout(() => onSceneDone?.(), SCENE_MS);
    return () => clearTimeout(t);
  }, [sceneActive, onSceneDone, SCENE_MS, companyScene]);

  useEffect(() => {
    if (overlayVisible) hasAnimatedInRef.current = true;
  }, [overlayVisible]);

  const shouldAnimateIn = overlayVisible && !hasAnimatedInRef.current;

  const topBarVariants = {
    hidden: { opacity: 0, y: -30, filter: "blur(2px)" },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.42, ease: "easeOut" },
    },
  };

  const leftPanelVariants = {
    hidden: { opacity: 1, scaleY: 0, filter: "blur(2px)" },
    show: {
      opacity: 1,
      scaleY: 1,
      filter: "blur(0px)",
      transition: { duration: 0.55, ease: "easeOut", delay: 0.42 },
    },
  };

  const userVariants = {
    hidden: { opacity: 0, y: 10, filter: "blur(2px)" },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.35, ease: "easeOut", delay: 0.42 + 0.55 },
    },
  };

  const pillCollapse = (delay = 0) => ({
    initial: { opacity: 1, y: 0, scaleY: 1 },
    animate:
      companyPhase >= 2
        ? {
            opacity: 0,
            y: -14,
            scaleY: 0.15,
            transition: { duration: 0.35, ease: "easeInOut", delay },
          }
        : { opacity: 1, y: 0, scaleY: 1 },
  });

  const effectivePlatformKey = companyScene
    ? companySceneKey || platformFormKey
    : platformFormKey;

  return (
    <div className="chat__animationWrapper">
      <motion.div
        className="chat__animationWrapper__animation"
        initial={
          skipReplayAnimations
            ? false
            : { rotateY: 0, scale: 0.8, z: -320, opacity: 1 }
        }
        animate={
          skipReplayAnimations
            ? { rotateY: 0, scale: 1, z: 0, opacity: 1 }
            : {
                rotateY: [0, -180, 0],
                scale: [0.8, 0.9, 1],
                z: [-320, -80, 0],
              }
        }
        transition={
          skipReplayAnimations
            ? { duration: 0 }
            : {
                duration: 1.7,
                ease: "easeInOut",
                times: [0, 0.45, 1],
              }
        }
      />

      {overlayVisible && (
        <div className="chat__animationWrapper__overlay">
          <motion.div
            className="chat__animationWrapper__overlay__topBar"
            variants={topBarVariants}
            initial={shouldAnimateIn ? "hidden" : false}
            animate={
              shouldAnimateIn
                ? "show"
                : { opacity: 1, y: 0, filter: "blur(0px)" }
            }
          >
            <div className="chat__animationWrapper__overlay__topBar__left">
              {companyPhase >= 3 && (
                <motion.div
                  layoutId="userAvatar"
                  className="chat__animationWrapper__overlay__topBar__left__userDock"
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                >
                  <UserAnimation />
                </motion.div>
              )}
            </div>
            <div className="chat__animationWrapper__overlay__topBar__right">
              {companyPhase >= 1 && effectivePlatformKey && (
                <div className="chat__animationWrapper__overlay__topBar__right__platformRow">
                  <motion.div
                    layoutId="platformForm"
                    className={[
                      "chat__animationWrapper__overlay__platformForm",
                      `chat__animationWrapper__overlay__platformForm--${effectivePlatformKey}`,
                      "isDocked",
                    ].join(" ")}
                    transition={{ type: "spring", stiffness: 260, damping: 22 }}
                  >
                    {effectivePlatformKey === "telegram" && (
                      <img src="/images/TelegramIconTopBar.svg" alt="" />
                    )}
                    {effectivePlatformKey === "whatsapp" && (
                      <img src="/images/WhatsappIconTopBar.svg" alt="" />
                    )}
                    {effectivePlatformKey === "email" && (
                      <img src="/images/EnvelopeIconTopBar.svg" alt="" />
                    )}
                  </motion.div>

                  <motion.div
                    className="chat__animationWrapper__overlay__topBar__right__platformRow__companyBlock"
                    initial={{ width: 0, opacity: 0 }}
                    animate={
                      companyPhase >= 1
                        ? { width: 185, opacity: 1 }
                        : { width: 0, opacity: 0 }
                    }
                    transition={{
                      duration: 0.45,
                      ease: "easeOut",
                      delay: 0.12,
                    }}
                  >
                    <img src="/images/search.svg" alt="Search" />

                    <img src="/images/settings.svg" alt="Settings" />
                  </motion.div>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="chat__animationWrapper__overlay__leftPanel"
            variants={leftPanelVariants}
            style={{ transformOrigin: "top" }}
            initial={shouldAnimateIn ? "hidden" : false}
            animate={
              shouldAnimateIn
                ? "show"
                : { opacity: 1, scaleY: 1, filter: "blur(0px)" }
            }
          />

          {companyPhase < 3 && (
            <motion.div
              layoutId="userAvatar"
              className="chat__animationWrapper__overlay__user"
              variants={userVariants}
              initial={shouldAnimateIn ? "hidden" : false}
              animate={
                shouldAnimateIn
                  ? "show"
                  : { opacity: 1, y: 0, filter: "blur(0px)" }
              }
            >
              <UserAnimation />
            </motion.div>
          )}

          {companyPhase < 1 && platformFormKey && (
            <motion.div
              layoutId="platformForm"
              className={[
                "chat__animationWrapper__overlay__platformForm",
                `chat__animationWrapper__overlay__platformForm--${platformFormKey}`,
              ].join(" ")}
              initial={{
                opacity: 0,
                x: 140,
                y: -26,
                rotate: 4,
                scale: 0.92,
                filter: "blur(6px)",
              }}
              animate={{
                opacity: 1,
                x: 0,
                y: 0,
                rotate: 0,
                scale: 1,
                filter: "blur(0px)",
              }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 22,
                mass: 0.7,
                delay: 0.1,
              }}
            >
              {platformFormKey === "telegram" && (
                <img src="/images/TelegramForm.svg" alt="" />
              )}
              {platformFormKey === "whatsapp" && (
                <img src="/images/WhatsappForm.svg" alt="" />
              )}
              {platformFormKey === "email" && (
                <img src="/images/EmailForm.svg" alt="" />
              )}
            </motion.div>
          )}

          {phoneVisible && (
            <div className="chat__animationWrapper__overlay__phone">
              <motion.div
                className="chat__animationWrapper__overlay__phone__pill"
                style={{ top: 283, left: 55, transformOrigin: "bottom" }}
                initial={{ opacity: 0, x: 31, width: 76 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  width: "calc(242px - 56px )",
                }}
                transition={{
                  opacity: { duration: 0.25, ease: "easeOut" },
                  x: { duration: 0.45, ease: "easeInOut", delay: 0.05 },
                  width: { duration: 0.55, ease: "easeInOut", delay: 0.55 },
                }}
                {...pillCollapse(0.22)}
              >
                <motion.div
                  className="chat__animationWrapper__overlay__phone__pill__icon"
                  initial={{ opacity: 0, x: 64 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    opacity: { duration: 0.25, ease: "easeOut", delay: 0.15 },
                    x: { duration: 0.6, ease: "easeInOut", delay: 0.25 },
                  }}
                >
                  <Phone />
                </motion.div>

                <motion.div
                  className="chat__animationWrapper__overlay__phone__pill__text"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut", delay: 1.2 }}
                >
                  Contact
                </motion.div>
              </motion.div>
            </div>
          )}

          {authVisible && (
            <div className="chat__animationWrapper__overlay__auth">
              <motion.div
                className="chat__animationWrapper__overlay__auth__pill"
                style={{ top: 337, left: 55, transformOrigin: "bottom" }}
                initial={{ opacity: 0, x: 31, width: 76 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  width: "calc(242px - 56px)",
                }}
                transition={{
                  opacity: { duration: 0.25, ease: "easeOut" },
                  x: { duration: 0.45, ease: "easeInOut", delay: 0.05 },
                  width: { duration: 0.55, ease: "easeInOut", delay: 0.55 },
                }}
                {...pillCollapse(0.12)}
              >
                <motion.div
                  className="chat__animationWrapper__overlay__auth__pill__icon"
                  initial={{ opacity: 0, x: 64 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    opacity: { duration: 0.25, ease: "easeOut", delay: 0.15 },
                    x: { duration: 0.6, ease: "easeInOut", delay: 0.25 },
                  }}
                >
                  <Auth />
                </motion.div>

                <motion.div
                  className="chat__animationWrapper__overlay__auth__pill__text"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut", delay: 1.2 }}
                >
                  Verif code
                </motion.div>
              </motion.div>
            </div>
          )}

          {emailVisible && (
            <div className="chat__animationWrapper__overlay__email">
              <motion.div
                className="chat__animationWrapper__overlay__email__pill"
                style={{ top: 391, left: 55, transformOrigin: "bottom" }}
                initial={{ opacity: 0, x: 31, width: 76 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  width: "calc(242px - 52px)",
                }}
                transition={{
                  opacity: { duration: 0.25, ease: "easeOut" },
                  x: { duration: 0.45, ease: "easeInOut", delay: 0.05 },
                  width: { duration: 0.55, ease: "easeInOut", delay: 0.55 },
                }}
                {...pillCollapse(0)}
              >
                <motion.div
                  className="chat__animationWrapper__overlay__email__pill__icon"
                  initial={{ opacity: 0, x: 64 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    opacity: { duration: 0.25, ease: "easeOut", delay: 0.15 },
                    x: { duration: 0.6, ease: "easeInOut", delay: 0.25 },
                  }}
                >
                  <Mail />
                </motion.div>

                <motion.div
                  className="chat__animationWrapper__overlay__email__pill__text"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut", delay: 1.2 }}
                >
                  Email
                </motion.div>
              </motion.div>
            </div>
          )}

          <AnimatePresence>
            {companyPhase >= 4 && (
              <motion.div
                className="chat__animationWrapper__overlay__rightBlocks"
                initial={{ opacity: 0, x: 22, filter: "blur(6px)" }}
                animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, x: 18, filter: "blur(6px)" }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              >
                <div className="chat__animationWrapper__overlay__rightBlocks__topRow">
                  <motion.div
                    className="chat__animationWrapper__overlay__rightBlocks__topRow__box"
                    initial={{ opacity: 0, y: 10, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                  <motion.div
                    className="chat__animationWrapper__overlay__rightBlocks__topRow__box"
                    initial={{ opacity: 0, y: 10, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.35, ease: "easeOut", delay: 0.1 }}
                  />
                </div>

                <motion.div
                  className="chat__animationWrapper__overlay__rightBlocks__topRow__rect"
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.35, ease: "easeOut", delay: 0.2 }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
